import { useCallback, useEffect, useState } from 'react';
import { Bookmark, Edit3, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import PollBuilder, { type PollBuilderValues } from './PollBuilder';
import {
  AUTO_SEND_OFFSETS_MINUTES,
  deletePollTemplate,
  getPollTemplates,
  savePollTemplate,
  updatePollTemplate,
  type PollArrival,
  type PollTemplate,
} from '../../services/polls';
import { describeArrival, describeOffset } from '../../services/pollLibrary';
import { getTemplatesByDjId, type EventTemplate } from '../../services/templates';

interface ComesAlong {
  isDefault: boolean;
  eventTemplateIds: string[];
  arrival: PollArrival;
  arrivalOffsetMinutes: number;
}

const NEW_POLL_COMES_ALONG: ComesAlong = { isDefault: false, eventTemplateIds: [], arrival: 'draft', arrivalOffsetMinutes: 0 };

const SCHEDULED_ORDER: readonly number[] = [0, ...AUTO_SEND_OFFSETS_MINUTES.filter(offset => offset !== 0)];

function arrivalValue(comesAlong: ComesAlong): string {
  return comesAlong.arrival === 'scheduled' ? `scheduled:${comesAlong.arrivalOffsetMinutes}` : comesAlong.arrival;
}

function parseArrivalValue(value: string): Pick<ComesAlong, 'arrival' | 'arrivalOffsetMinutes'> {
  if (value.startsWith('scheduled:')) return { arrival: 'scheduled', arrivalOffsetMinutes: Number(value.slice('scheduled:'.length)) };
  return { arrival: value === 'live' ? 'live' : 'draft', arrivalOffsetMinutes: 0 };
}

interface EditorState {
  template: PollTemplate | null;
}

export function PollLibraryPanel({ djId }: { djId: string }) {
  const [templates, setTemplates] = useState<PollTemplate[]>([]);
  const [eventTemplates, setEventTemplates] = useState<EventTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [comesAlong, setComesAlong] = useState<ComesAlong>(NEW_POLL_COMES_ALONG);

  const loadLibrary = useCallback(async () => {
    try {
      const [pollTemplates, djEventTemplates] = await Promise.all([getPollTemplates(djId), getTemplatesByDjId(djId)]);
      setTemplates(pollTemplates);
      setEventTemplates(djEventTemplates);
      setLoadFailed(false);
    } catch (err) {
      console.error('[PollLibraryPanel] Could not load the poll library:', err);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [djId]);

  useEffect(() => {
    loadLibrary();
  }, [loadLibrary]);

  const openEditor = (template: PollTemplate | null) => {
    setComesAlong(template
      ? { isDefault: template.isDefault, eventTemplateIds: template.eventTemplateIds, arrival: template.arrival, arrivalOffsetMinutes: template.arrivalOffsetMinutes }
      : NEW_POLL_COMES_ALONG);
    setEditor({ template });
  };

  const closeEditor = () => setEditor(null);

  const handleSave = async (values: PollBuilderValues) => {
    const input = { title: values.title, questions: values.questions, settings: values.settings, ...comesAlong };
    if (editor?.template) {
      await updatePollTemplate(editor.template.id, input);
    } else {
      await savePollTemplate(djId, input);
    }
    toast.success('Saved to your library');
    closeEditor();
    await loadLibrary();
  };

  const handleDelete = async (template: PollTemplate) => {
    if (!confirm(`Delete "${template.title}" from your library? Events that already have it keep their copy.`)) return;
    try {
      await deletePollTemplate(template.id);
      setTemplates(prev => prev.filter(t => t.id !== template.id));
      toast.success('Poll deleted from your library');
    } catch (err) {
      console.error('[PollLibraryPanel] Could not delete a poll template:', err);
      toast.error('Failed to delete poll');
    }
  };

  const toggleEventTemplate = (eventTemplateId: string) => {
    setComesAlong(prev => ({
      ...prev,
      eventTemplateIds: prev.eventTemplateIds.includes(eventTemplateId)
        ? prev.eventTemplateIds.filter(id => id !== eventTemplateId)
        : [...prev.eventTemplateIds, eventTemplateId],
    }));
  };

  const eventTemplateName = (eventTemplateId: string) => eventTemplates.find(t => t.id === eventTemplateId)?.name;

  const comesAlongSection = (
    <div className="space-y-3 bg-gray-50 dark:bg-gray-800 rounded-lg px-4 py-3 border border-gray-200 dark:border-gray-700">
      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">Comes along</p>
      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
        <input
          type="checkbox"
          checked={comesAlong.isDefault}
          onChange={e => setComesAlong(prev => ({ ...prev, isDefault: e.target.checked }))}
        />
        Add to every new event
      </label>
      <div>
        <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">With these event templates</p>
        {eventTemplates.length === 0 ? (
          <p className="text-xs text-gray-500">You have no event templates yet. Save one from an event's settings page.</p>
        ) : (
          <div className="space-y-1">
            {eventTemplates.map(eventTemplate => (
              <label key={eventTemplate.id} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={comesAlong.eventTemplateIds.includes(eventTemplate.id)}
                  onChange={() => toggleEventTemplate(eventTemplate.id)}
                />
                {eventTemplate.name}
              </label>
            ))}
          </div>
        )}
      </div>
      <div>
        <label htmlFor="poll-arrival" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Arrives as</label>
        <select
          id="poll-arrival"
          className="input w-full"
          value={arrivalValue(comesAlong)}
          onChange={e => setComesAlong(prev => ({ ...prev, ...parseArrivalValue(e.target.value) }))}
        >
          <option value="draft">Draft</option>
          <option value="live">Live from the start</option>
          {SCHEDULED_ORDER.map(offset => (
            <option key={offset} value={`scheduled:${offset}`}>
              {offset === 0 ? 'At the start' : describeOffset(offset).replace(/^./, letter => letter.toUpperCase())}
            </option>
          ))}
        </select>
        {comesAlong.arrival === 'scheduled' && (
          <p className="text-xs text-gray-500 mt-1">
            VoteBeats sends it at that time even if your page is closed. If the time has already passed when the event
            is created, it goes out right away. It's never sent after the event has ended.
          </p>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">My Polls</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Saved polls you can reuse. Changes here don't affect events already created.
          </p>
        </div>
        <button onClick={() => openEditor(null)} className="btn btn-primary btn-sm flex items-center gap-1 flex-shrink-0">
          <Plus className="w-4 h-4" /> New poll
        </button>
      </div>

      {loading && <p className="text-sm text-gray-500">Loading your polls…</p>}

      {!loading && loadFailed && (
        <p className="text-sm text-danger">Couldn't load your poll library. Reload to try again.</p>
      )}

      {!loading && !loadFailed && templates.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No saved polls yet. Create one here, or save a poll from an event's Messages page.
        </p>
      )}

      {!loading && !loadFailed && templates.length > 0 && (
        <div className="space-y-2">
          {templates.map(template => (
            <div key={template.id} className="flex items-center justify-between gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="min-w-0">
                <p className="font-medium text-sm text-gray-900 dark:text-white truncate flex items-center gap-1">
                  <Bookmark className="w-3 h-3 text-primary flex-shrink-0" /> {template.title}
                </p>
                <div className="flex flex-wrap items-center gap-2 mt-0.5">
                  <span className="text-xs text-gray-500">
                    {template.questions.length} question{template.questions.length !== 1 ? 's' : ''}
                  </span>
                  {template.isDefault && (
                    <span className="text-xs px-1.5 py-0.5 rounded-full font-medium bg-primary/10 text-primary">Default</span>
                  )}
                  {template.eventTemplateIds.map(eventTemplateId => {
                    const name = eventTemplateName(eventTemplateId);
                    if (!name) return null;
                    return (
                      <span key={eventTemplateId} className="text-xs px-1.5 py-0.5 rounded-full font-medium bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-400">
                        {name}
                      </span>
                    );
                  })}
                  <span className="text-xs text-gray-500">{describeArrival(template)}</span>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => openEditor(template)}
                  className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 transition-colors"
                  title="Edit"
                  aria-label={`Edit ${template.title}`}
                >
                  <Edit3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(template)}
                  className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-500 transition-colors"
                  title="Delete"
                  aria-label={`Delete ${template.title}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={closeEditor}>
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full max-h-[85vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {editor.template ? 'Edit library poll' : 'New library poll'}
            </h3>
            <PollBuilder
              variant="library"
              onSubmit={handleSave}
              onCancel={closeEditor}
              initialTitle={editor.template?.title}
              initialQuestions={editor.template?.questions}
              initialSettings={editor.template?.settings}
              extraSettings={comesAlongSection}
            />
          </div>
        </div>
      )}
    </div>
  );
}
